import React, { useMemo, useState } from 'react'
import { INode } from 'shared/types/tree'
import { probeImage } from './utility'
import { doInBatches } from '../../../utility'
import { Column } from 'shared/ui/Table/types'
import { Box, Button } from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import {
  ContentFilterType,
  FilterType,
  NodeFilterType,
} from '../treeManipulation/types'
import { FilteredTreeTable } from '../treeManipulation/FilteredTreeTable'
import { getFilteredResults } from '../treeManipulation/getFilteredResults'
import Tooltip from '@mui/material/Tooltip'

export function ImageUrlCheck({
  toCheck,
  tree,
}: {
  toCheck: string[]
  tree: INode
}) {
  const [checking, setChecking] = useState(false)
  const [badUrls, setBadUrls] = useState<string[] | undefined>()

  const checkUrls = async () => {
    setChecking(true)
    setBadUrls(undefined)

    const doCheck = async (toCheck: string) => {
      const { ok, url } = await probeImage(toCheck)
      if (!ok) {
        setBadUrls((prev) => {
          if (!prev) return [url]
          return [...prev, url]
        })
      }
    }
    await doInBatches(toCheck, doCheck)
    setBadUrls((prev) => prev ?? [])
    setChecking(false)
  }

  const columns = useMemo<Column<string>[]>(() => {
    return [
      {
        title: 'URL',
        rowContent: (u: string) => u,
      },
    ]
  }, [])

  if (!toCheck.length) return null

  return (
    <Box>
      <strong>Image URL Check</strong>
      <Button onClick={checkUrls} disabled={checking}>
        {checking ? 'Checking…' : 'Check'}
      </Button>
      {!!badUrls?.length && (
        <Box>
          <Table<string>
            title={`${badUrls.length} Bad`}
            columns={columns}
            items={badUrls}
          />
        </Box>
      )}
      {badUrls && !badUrls.length && <Box>Looks All Good</Box>}
      {badUrls?.length && <NodesWithUrls tree={tree} urls={badUrls} />}
    </Box>
  )
}

function NodesWithUrls({ tree, urls }: { tree: INode; urls: string[] }) {
  const { filter, urlMap } = useMemo(() => {
    const withContent: ContentFilterType = urls.reduce<ContentFilterType>(
      (acc: ContentFilterType, url: string) => {
        return {
          ...acc,
          [url]: { filterType: 'including' },
        }
      },
      {},
    )
    withContent.ALL_OR = true
    const filter: NodeFilterType = {
      filterType: FilterType.node,
      withContent,
    }

    const nodes = getFilteredResults<INode>(filter, tree)
    const urlMap = nodes.reduce<Record<string, string[]>>(
      (acc, { id, nodes: _, ...rest }) => {
        const asString = JSON.stringify(rest)
        return {
          ...acc,
          [id]: urls.filter((u) => asString.includes(u)),
        }
      },
      {},
    )

    return { filter, urlMap }
  }, [tree, urls])

  const urlCol = useMemo(() => {
    return [
      {
        title: 'Urls',
        rowContent(item: INode) {
          return (
            <Tooltip title={<URLTooltip urls={urlMap[item.id]} />}>
              <Box>{`URLS: ${urlMap[item.id].length}`}</Box>
            </Tooltip>
          )
        },
        rowString(item: INode) {
          return urlMap[item.id].join(';')
        },
      },
    ]
  }, [urls])

  return (
    <FilteredTreeTable
      filter={filter}
      treeId={tree.tree_id}
      versionId={tree.version}
      customColumns={urlCol}
    />
  )
}

function URLTooltip({ urls }: { urls: string[] }) {
  return (
    <Box>
      {urls.map((url) => (
        <Box key={url}>{url}</Box>
      ))}
    </Box>
  )
}
